/**
 * FinancialTable — Period metrics table (MTD/QTD/YTD).
 * Column order matches the email template: Actuals, Actuals vs Prorated Budget,
 * Budget To Go, Monthly Budget, Prior Year Full, Actuals vs PY To Date, Open Orders,
 * and optionally Forecast.
 */

import { PeriodMetrics } from "../utils/PowerBIApiCall";
import { CalcTooltip } from "./CalcTooltip";
import { formatCurrency, formatPctSigned as formatPct } from "../utils/format";
import {
  performanceColor as getPerformanceColor,
  SERIES,
} from "../utils/chartTheme";

interface PeriodRow {
  label: string;
  metrics: PeriodMetrics;
  /** Override forecast with regression model value (e.g. from forecastModel) */
  regressionForecast?: number;
}

interface FinancialTableProps {
  rows: PeriodRow[];
  /** When provided, makes Open Orders values clickable (opens order detail modal) */
  onOpenOrderClick?: () => void;
  /** Show the Forecast column (default: false) */
  showForecast?: boolean;
}

/**
 * Last-resort forecast for a row whose caller supplied no regressionForecast
 * (no usable daily trend). Flat month-to-date rate, open orders EXCLUDED to
 * match the canonical model in utils/forecastModel.
 */
function computeForecast(metrics: PeriodMetrics): number {
  const avgDaily =
    metrics.businessDaysElapsed > 0
      ? metrics.actual / metrics.businessDaysElapsed
      : 0;
  const remainingDays = metrics.businessDaysTotal - metrics.businessDaysElapsed;
  return metrics.actual + avgDaily * remainingDays;
}

function ProgressBar({ pct, color }: { pct: number; color: string }) {
  return (
    <div className="w-full h-1.5 bg-gray-200 rounded-full mt-1">
      <div
        className="h-full rounded-full transition-all"
        style={{
          width: `${Math.min(Math.max(pct, 0), 100)}%`,
          backgroundColor: color,
        }}
      />
    </div>
  );
}

const TH =
  "text-2xs font-bold tracking-wider text-gray-500 uppercase px-4 py-3";

export function FinancialTable({
  rows,
  onOpenOrderClick,
  showForecast = false,
}: FinancialTableProps) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-wmp-50/70 border-b border-gray-200">
            <th className={`text-left ${TH}`}>Period</th>
            <th className={`text-right ${TH}`}>Actuals</th>
            <th className={`text-center ${TH} min-w-[140px]`}>
              Actuals vs Prorated Budget
            </th>
            <th className={`text-right ${TH}`}>Budget To Go</th>
            <th className={`text-right ${TH}`}>Monthly Budget</th>
            <th className={`text-right ${TH}`}>Prior Year Full Month Actual</th>
            <th className={`text-center ${TH} min-w-[120px]`}>
              Actuals vs PY To Date
            </th>
            <th className={`text-right ${TH}`}>Open Orders</th>
            {showForecast && <th className={`text-right ${TH}`}>Forecast</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const m = row.metrics;

            // Forecast (regression or simple avg-daily)
            const simpleForecast = computeForecast(m);
            const forecast = row.regressionForecast ?? simpleForecast;
            const useRegression = row.regressionForecast != null;
            const avgDaily =
              m.businessDaysElapsed > 0 ? m.actual / m.businessDaysElapsed : 0;
            const remainingDays = m.businessDaysTotal - m.businessDaysElapsed;

            // Forecast color: compared against Budget (Full)
            const forecastVsFullPct =
              m.budgetFull > 0 ? (forecast / m.budgetFull) * 100 : 0;
            const forecastColor = getPerformanceColor(
              forecastVsFullPct,
              m.budgetFull > 0,
            );

            // Budget To Go = Actuals - Full Budget
            const budgetToGo = m.actual - m.budgetFull;

            // Actuals vs Prorated Budget
            const pacingPct =
              m.budgetProrated > 0 ? (m.actual / m.budgetProrated) * 100 : 0;
            const pacingColor = getPerformanceColor(
              pacingPct,
              m.budgetProrated > 0,
            );

            // Actuals vs PY To Date (uses prorated PY to match elapsed period)
            const pyToDate = m.priorYearProrated;
            const vsPYToDatePct =
              pyToDate > 0 ? ((m.actual - pyToDate) / pyToDate) * 100 : 0;
            const pyRatio = pyToDate > 0 ? (m.actual / pyToDate) * 100 : 0;
            const vsPYColor =
              vsPYToDatePct >= 0 ? SERIES.positive : SERIES.negative;
            const hasPY = pyToDate > 0;

            return (
              <tr
                key={row.label}
                className="bg-white hover:bg-wmp-50/50 transition-colors border-b border-gray-100 last:border-b-0"
              >
                {/* Period */}
                <td className="px-4 py-5">
                  <span className="inline-block bg-wmp-100 text-wmp-700 px-3 py-1 rounded-full text-xs font-bold uppercase">
                    {row.label}
                  </span>
                  <span className="block text-[10px] text-gray-400 mt-1">
                    {m.businessDaysElapsed} of {m.businessDaysTotal} days
                  </span>
                </td>

                {/* Actuals */}
                <td className="px-4 py-5 text-base text-gray-700 text-right font-medium tabular-nums">
                  {formatCurrency(m.actual)}
                </td>

                {/* Actuals vs Prorated Budget */}
                <td className="px-4 py-5 text-center">
                  <CalcTooltip
                    formula="Actuals / Prorated Budget"
                    values={`= ${formatCurrency(m.actual)} / ${formatCurrency(m.budgetProrated)} = ${pacingPct.toFixed(1)}%`}
                  >
                    <div className="inline-block min-w-[100px]">
                      <span
                        className="text-lg font-bold tabular-nums"
                        style={{ color: pacingColor }}
                      >
                        {pacingPct.toFixed(1)}%
                      </span>
                      <ProgressBar pct={pacingPct} color={pacingColor} />
                      <span className="text-[10px] text-gray-400 mt-0.5 block">
                        {formatCurrency(m.budgetProrated)}
                      </span>
                    </div>
                  </CalcTooltip>
                </td>

                {/* Budget To Go */}
                <td className="px-4 py-5 text-right">
                  <CalcTooltip
                    formula="Actuals - Full Budget"
                    values={`= ${formatCurrency(m.actual)} - ${formatCurrency(m.budgetFull)} = ${formatCurrency(budgetToGo)}`}
                  >
                    <span
                      className={`text-base font-medium tabular-nums ${budgetToGo >= 0 ? "text-emerald-600" : "text-red-600"}`}
                    >
                      {formatCurrency(budgetToGo)}
                    </span>
                  </CalcTooltip>
                </td>

                {/* Monthly Budget */}
                <td className="px-4 py-5 text-base text-gray-700 text-right font-medium tabular-nums">
                  {formatCurrency(m.budgetFull)}
                </td>

                {/* Prior Year Full */}
                <td className="px-4 py-5 text-base text-gray-700 text-right tabular-nums">
                  {formatCurrency(m.priorYearFull)}
                </td>

                {/* Actuals vs PY To Date */}
                <td className="px-4 py-5 text-center">
                  <CalcTooltip
                    formula="(Actuals - PY To Date) / PY To Date"
                    values={`= (${formatCurrency(m.actual)} - ${formatCurrency(pyToDate)}) / ${formatCurrency(pyToDate)} = ${formatPct(vsPYToDatePct)}`}
                  >
                    <div className="inline-block min-w-[100px]">
                      <span
                        className="text-lg font-bold tabular-nums"
                        style={{ color: hasPY ? vsPYColor : SERIES.neutral }}
                      >
                        {formatPct(vsPYToDatePct)}
                      </span>
                      <ProgressBar
                        pct={pyRatio}
                        color={hasPY ? vsPYColor : SERIES.neutral}
                      />
                      <span className="text-[10px] text-gray-400 mt-0.5 block">
                        PY: {formatCurrency(pyToDate)}
                      </span>
                    </div>
                  </CalcTooltip>
                </td>

                {/* Open Orders */}
                <td className="px-4 py-5 text-base text-right">
                  {onOpenOrderClick && m.openOrders > 0 ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenOrderClick();
                      }}
                      className="text-emerald-600 hover:text-emerald-800 underline decoration-dotted underline-offset-2 cursor-pointer tabular-nums font-medium"
                      title="View open order detail"
                    >
                      {formatCurrency(m.openOrders)}
                    </button>
                  ) : (
                    <span className="text-emerald-600 font-medium tabular-nums">
                      {formatCurrency(m.openOrders)}
                    </span>
                  )}
                </td>

                {/* Forecast */}
                {showForecast && (
                  <td className="px-4 py-5 text-right">
                    <CalcTooltip
                      formula={
                        useRegression
                          ? "Linear regression forecast (medium)"
                          : "Act + (Avg Daily x Remaining Days)"
                      }
                      values={
                        useRegression
                          ? `Regression model projection = ${formatCurrency(forecast)}`
                          : `= ${formatCurrency(m.actual)} + (${formatCurrency(avgDaily)} x ${remainingDays} days)`
                      }
                    >
                      <span
                        className="text-base font-semibold tabular-nums"
                        style={{ color: forecastColor }}
                      >
                        {formatCurrency(forecast)}
                      </span>
                    </CalcTooltip>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {/* Footnote — inside overflow container so it matches table width */}
      <div className="px-4 py-3 border-t border-gray-100 flex flex-wrap justify-between gap-x-6 min-w-fit">
        <p className="text-[11px] text-gray-400 italic whitespace-nowrap">
          Budget To Go = Actuals &minus; Full Budget
        </p>
        <p className="text-[11px] text-gray-400 italic whitespace-nowrap">
          Actuals vs Prorated Budget = Actuals &divide; Prorated Budget
          &nbsp;|&nbsp; Actuals vs PY To Date = YoY change using actual PY
          period actuals
        </p>
      </div>
    </div>
  );
}

/**
 * Variance badge component — shows percentage variance with color.
 */
export function VarianceBadge({
  value,
  label,
  dollarAmount,
  formula,
  formulaValues,
}: {
  value: number;
  label?: string;
  dollarAmount?: number;
  /** Formula description for rich tooltip (line 1) */
  formula?: string;
  /** Substituted values for rich tooltip (line 2) */
  formulaValues?: string;
}) {
  const isPositive = value >= 0;
  const colorClass = isPositive ? "text-emerald-600" : "text-red-600";
  const bgClass = isPositive ? "bg-emerald-50" : "bg-red-50";

  const badge = (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colorClass} ${bgClass}`}
    >
      {formatPct(value)}
      {label && <span className="ml-1 text-gray-500">{label}</span>}
    </span>
  );

  if (formula && formulaValues) {
    return (
      <CalcTooltip formula={formula} values={formulaValues}>
        {badge}
      </CalcTooltip>
    );
  }

  return badge;
}
